import { Component, Injector, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ModalService } from 'projects/shared/modal/modal.service';
import { forkJoin, Observable } from 'rxjs';
import { allowsUnmarriedPartner, Borrower } from '../../../models/borrower-model';
import { ErrorMessage } from '../../../models/error-message.model';
import { MyInfoStep } from '../../../models/wizard/my-info-step.model';
import { BorrowerAddressHistoryComponent } from '../../borrower-address-history/borrower-address-history.component';
import { BorrowerInfoMiniComponent } from '../../borrower-info-mini/borrower-info-mini.component';
import { DependentsDialogComponent } from '../../dependents/dependents-dialog.component';
import { WizardStepComponentBase } from '../wizard-step-base.component';
import { EnumsService } from '../../../services/enums.service';
import { EnumerationItem } from 'projects/shared/models/enumeration-item.model';
import { Declarations } from '../../../models/declarations-model';
import { DomesticRelationshipTypeEnum } from '../../../models/mortgage.model';

@Component({
    selector: 'my-info',
    templateUrl: 'my-info-step.component.html',
    styleUrls: ['my-info-step.component.scss'],
    standalone: false
})
export class MyInfoStepComponent extends WizardStepComponentBase<MyInfoStep> implements OnInit {

  @ViewChildren('borrowerInfo') borrowerInfoComponents: QueryList<BorrowerInfoMiniComponent> | undefined;
  @ViewChild('myInfoForm') myInfoForm: NgForm | undefined;
  @ViewChild(BorrowerAddressHistoryComponent) borrowerAddressHistoryComponent: BorrowerAddressHistoryComponent | undefined;

  public coBorrowerToDelete: Borrower | undefined = undefined;

  borrower!: Borrower;

  dependentAges: string = null;

  noOfDependents: number = 0;

  coBorrowers: Borrower[] = [];

  ssnPattern = { '0': { pattern: new RegExp('\\d'), symbol: 'X' } };

  borrowerSettings: Map<number, boolean> = new Map<number, boolean>();

  get languagePreferenceOptions(): EnumerationItem[] {
    return this._enumService.languages;
  }

  // WI 24451 — unmarried-partner gate + option sources. The gate is a LOCAL/derived UI flag (mirrors URLA
  // contact-info, where it's also local): MortgageBorrower has no persisted "has partner" boolean, so on load
  // it's reconstructed from the relationship fields below. Known limitation (Codex review): if an admin shows
  // the gate but leaves Type/State hidden/blank, a bare "yes" isn't retained on reload — by design, since the
  // persisted, meaningful data is the relationship Type/State, not the checkbox. Persisting it independently
  // would require a new MortgageBorrower field (backend), out of scope here.
  unmarriedAddendum: boolean = false;
  readonly DomesticRelationshipTypeEnum = DomesticRelationshipTypeEnum;
  get domesticRelationshipTypeOptions() { return this._enumService.domesticRelationshipTypes; }
  get usStates() { return this._enumService.states; }

  /** The gate (and group) shows only for an unmarried borrower and when an admin hasn't hidden it. */
  get showUnmarriedPartnerGate(): boolean {
    return allowsUnmarriedPartner(this.borrower?.maritalStatus) && !this.isHidden('borrower.unmarriedAddendum');
  }
  /** Type/State/Specify show only once the borrower confirms they have an unmarried partner. */
  get showUnmarriedPartnerDetails(): boolean {
    return this.showUnmarriedPartnerGate && this.unmarriedAddendum;
  }

  constructor(
    private readonly _modalService: ModalService,
    private readonly _enumService: EnumsService,
    private readonly _injector: Injector) {
    super(_injector);
    this.saveMortgageApplicationBeforeNextStep = true;
  }

  private showDependentsPopup(dependentAges: number[]) {
    const modal = this._modalService.show(DependentsDialogComponent);
    modal.componentInstance.dependentAges = dependentAges;

    modal.result.then(dependentAges => {
      this.borrower.dependents = dependentAges;
      this.noOfDependents = dependentAges.length;
      this.dependentAges = this.borrower.dependents.join(', ');
      this.cdr.detectChanges();
    }, error => {
      this.noOfDependents = this.borrower.dependents.length;
      this.dependentAges = this.borrower.dependents.join(', ');
      this.cdr.detectChanges();
    })
  }

  private validate = (): boolean => {
    let valid = false;
    if (this.myInfoForm) {
      this.myInfoForm.form.markAllAsTouched();
      valid = this.myInfoForm.form.valid;
    }
    if (this.borrowerInfoComponents) {
      for (const borrowerinfo of this.borrowerInfoComponents) {
        valid = borrowerinfo.validate() && valid;
      }
    }
    if (this.step.configuresAddressHistory && this.borrowerAddressHistoryComponent) {
      valid = this.borrowerAddressHistoryComponent?.validateAddressHistory() && valid;
    }
    return valid;
  }

  onNextClicked() {
    const valid = this.validate();
    let firstInvalidOneId: string = null;
    if (!valid) {
      for (const key in this.myInfoForm.form.controls) {
        if (Object.prototype.hasOwnProperty.call(this.myInfoForm.form.controls, key)) {
          if (this.myInfoForm.form.controls[key].status === 'INVALID') {
            firstInvalidOneId = key;
            break;
          }
        }
      }
    }
    if (firstInvalidOneId) {
      this.scrollToElement(firstInvalidOneId);
    }

    if (valid) {
      this.saveMortgageApplicationBeforeNextStep = false;
      if (this.wizardFlowService.isEditEnabled) {
        super.onNextClicked();
        return;
      }
      this.startSpinner();
      // The form now has a dedicated Home Phone field, so don't clobber a borrower-entered
      // home phone with the mobile value. Only fall back to mobile when home phone is empty
      // (e.g. the Home Phone field is hidden by admin config) so the backend still gets a value.
      this.mortgageApplication.borrowers.forEach(b => {
        if (!b.homePhone) {
          b.homePhone = b.mobilePhone;
        }
      })
      this.wizardFlowService.saveMortgage().subscribe(result => {
        const calls: Observable<any>[] = [];
        if (this.borrowerInfoComponents) {
          for (const borrowerinfo of this.borrowerInfoComponents) {
            const bi = borrowerinfo.borrower;
            const insertedBorrower = result.borrowers.find((x: any) => x.firstName === bi.firstName && x.lastName === bi.lastName && x.primaryEmail === bi.primaryEmail);
            if (!borrowerinfo.mainBorrowerWillApplyForMe) {
              if (insertedBorrower) {
                borrowerinfo.setBorrowerIdContactId(insertedBorrower.borrowerId, insertedBorrower.contactId);
              }
              const borrowerInviteToCompleteAppCall = this.mortgageApplicationService.inviteToCompleteAppForCoborrower(this.mortgageApplication.mortgageId,
                this.mortgageApplication.applicationId, borrowerinfo.borrower.borrowerId!);
              calls.push(borrowerInviteToCompleteAppCall);
            } else {
              // Merge borrower needs to be called here for the borrower
              if (insertedBorrower) {
                const mergeBorrowerCall = this.mortgageApplicationService.mergeCoborrower(this.mortgageApplication.applicationId,
                  insertedBorrower.contactId);
                calls.push(mergeBorrowerCall);
              }
            }
          }
        }
        if (calls.length > 0) {
          forkJoin(calls).subscribe(
            (result) => {
              this.stopSpinner();
              super.onNextClicked();
            },
            (error) => {
              this.handleError(error);
            }
          );
        } else {
          this.stopSpinner();
          super.onNextClicked();
        }
      }, error => {
        this.handleError(error);
      });
    }
  }

  onNoOfDependentsChanged = () => {
    if (!this.noOfDependents) {
      this.borrower.dependents = [];
      this.dependentAges = "";
      return;
    }
    const dependents = [...this.borrower.dependents];
    const numberOfDependentsDiff = this.noOfDependents - dependents.length;
    if (numberOfDependentsDiff > 0) {
      for (let i = 1; i <= numberOfDependentsDiff; i++) {
        dependents.push(0);
      }
    } else if (numberOfDependentsDiff < 0) {
      const noOfDependents = dependents.length;
      for (let i = noOfDependents - 1; i > noOfDependents - 1 + numberOfDependentsDiff; i--) {
        dependents.splice(i, 1);
      }
    }
    this.showDependentsPopup(dependents);
  }

  onEditDependentsClicked = () => {
    const dependents = [...this.borrower.dependents];
    this.showDependentsPopup(dependents);
  }

  onAddCoBorrowerClicked = () => {
    const coBorrower = new Borrower();
    this.mortgageApplication.borrowers.push(coBorrower);
  }

  onCoBorrowerDeleteClicked = (coBorrower: Borrower) => {
    this.coBorrowerToDelete = coBorrower;
  }

  onCoBorrowerDeleteConfirmClicked = (coBorrower: Borrower) => {
    if (coBorrower.contactId && coBorrower.contactId > 0) {
      this.mortgageApplicationService.removeBorrower(this.mortgageApplication.applicationId, coBorrower.contactId).subscribe(() => {
        this.removeBorrowerFromMortgage(coBorrower);
      });
    } else {
      this.removeBorrowerFromMortgage(coBorrower);
    }
  }

  onLanguagePreferenceChanged = () => {
    if (this.borrower.languagePreference !== 'Other') {
      this.borrower.languagePreferenceOtherDescription = null;
    }
  }

  // WI 24451 — cleanup cascade mirroring URLA contact-info: leaving a status that allows an unmarried
  // partner clears the gate + children; unchecking the gate clears Type/State; non-Other clears the
  // Specify text. OPE-1: switching between Single and Other keeps the answers (same workflow).
  onMaritalStatusChanged = () => {
    if (!allowsUnmarriedPartner(this.borrower.maritalStatus)) {
      this.unmarriedAddendum = false;
      this.onUnmarriedAddendumChanged();
    }
  }

  onUnmarriedAddendumChanged = () => {
    if (!this.unmarriedAddendum) {
      this.borrower.domesticRelationshipType = null;
      this.borrower.unmarriedRelationshipState = null;
      this.onDomesticRelationshipTypeChanged();
    }
  }

  onDomesticRelationshipTypeChanged = () => {
    if (this.borrower.domesticRelationshipType !== DomesticRelationshipTypeEnum.Other) {
      this.borrower.unmarriedRelationshipTypeOther = null;
    }
  }

  private removeBorrowerFromMortgage = (borrower: Borrower): void => {
    const index = this.mortgageApplication.borrowers.indexOf(borrower);
    if (index >= 0) {
      this.mortgageApplication.borrowers.splice(index, 1);
      this.coBorrowerToDelete = undefined;
    }
  }

  private handleError = (error: any) => {
    this.stopSpinner();
    const errorMessage = new ErrorMessage("An error occurred while saving the mortgage", error?.error?.message ?? error?.message);
    this.showError(errorMessage);
    window.scroll(0, 0);
  }

  onCoBorrowerDeleteCancelClicked = (coBorrower: Borrower) => {
    this.coBorrowerToDelete = undefined;
  }

  ngOnInit() {
    this.borrowerSettings = this.wizardFlowService.context.borrowerSettings;
    if (this.mortgageApplication.borrowers && this.mortgageApplication.borrowers.length > 0) {
      if (this.currentBorrower) {
        this.borrower = this.currentBorrower;
        this.unmarriedAddendum = allowsUnmarriedPartner(this.borrower.maritalStatus)
          && !!(this.borrower.domesticRelationshipType
                || this.borrower.unmarriedRelationshipState
                || this.borrower.unmarriedRelationshipTypeOther);
        this.noOfDependents = this.borrower.dependents.length;
        this.dependentAges = this.borrower.dependents.join(', ');
        this.coBorrowers = this.mortgageApplication.borrowers.filter(b => b !== this.currentBorrower);
      }
    } else {
      this.borrower = new Borrower();
      this.mortgageApplication.borrowers.push(this.borrower);
    }
    this.fixUnsetValuesToNull();
  }

  scrollToElement = (name: string) => {
    const elements = document.getElementsByName(name);
    let element;
    if (elements) {
      element = elements[0]
    }
    if (element) {
      setTimeout(() => {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }, 250);
    }
  }

  private fixUnsetValuesToNull = () => {
    if (!this.borrower.nameSuffix) {
      this.borrower.nameSuffix = null;
    }
    if (!this.borrower.languagePreference) {
      this.borrower.languagePreference = null;
    }
    if (!this.borrower.maritalStatus) {
      this.borrower.maritalStatus = null;
    }
    if (!this.borrower.declarations) {
      this.borrower.declarations = new Declarations();
    } else {
      if (!this.borrower.declarations.residenceStatus) {
        this.borrower.declarations.residenceStatus = null;
      }
    }
  }
}
